import React from 'react';
import { ESSENTIALS_IMG } from '../media';

/* Ported from the Lovable build's src/components/EcosystemDiagram.tsx.
   Geometry unchanged. Colors read design system tokens. */

const CX = 300;
const CY = 300;
const R = 198;
const NODE_R = 62;
const CENTER_R = 112;
const polar = (r: number, deg: number): [number, number] => {
  const rad = deg * Math.PI / 180;
  const round = (v: number) => Math.round(v * 100) / 100;
  return [round(CX + r * Math.cos(rad)), round(CY + r * Math.sin(rad))];
};
const nodes = [{
  name: 'Air',
  img: ESSENTIALS_IMG.air,
  angle: -90
}, {
  name: 'Water',
  img: ESSENTIALS_IMG.water,
  angle: -18
}, {
  name: 'Food',
  img: ESSENTIALS_IMG.food,
  angle: 54
}, {
  name: 'Health',
  img: ESSENTIALS_IMG.health,
  angle: 126
}, {
  name: 'Materials',
  img: ESSENTIALS_IMG.materials,
  angle: 198
}];
const GAP = 23;
const arcs = nodes.map((n, i) => {
  const next = nodes[(i + 1) % nodes.length];
  const a1 = n.angle + GAP;
  const a2 = next.angle - GAP + (next.angle <= n.angle ? 360 : 0);
  const [x1, y1] = polar(R, a1);
  const [x2, y2] = polar(R, a2);
  return {
    id: `eco-arc-${i}`,
    d: `M ${x1.toFixed(1)} ${y1.toFixed(1)} A ${R} ${R} 0 0 1 ${x2.toFixed(1)} ${y2.toFixed(1)}`,
    stroke: i % 2 === 0 ? 'var(--gold)' : 'var(--forest-mid)',
    dot: i % 2 === 0 ? 'var(--forest-mid)' : 'var(--gold)',
    midAngle: (a1 + a2) / 2
  };
});
export function EcosystemDiagram() {
  return <svg viewBox="-100 -16 760 560" role="img" aria-label="The five interconnected essentials: air, water, food, materials and health, orbiting a centre that reads Nature's Intelligence plus Human Ingenuity" className="mx-auto w-full max-w-[540px]" data-id="element-101" data-mp="nv1yj7jqg2vv">
      <defs data-id="element-102" data-mp="a3n66a3xqpc5">
        {nodes.map((n, i) => <clipPath key={n.name} id={`eco-clip-${i}`} data-id="element-103" data-mp="a0r72igcpum4" data-mpi={i}>
            <circle cx={polar(R, n.angle)[0]} cy={polar(R, n.angle)[1]} r={NODE_R} data-id="element-104" data-mp="52pmtu76vp92" />
          </clipPath>)}
      </defs>

      {arcs.map(a => <path key={a.id} id={a.id} d={a.d} fill="none" stroke={a.stroke} strokeWidth={1.4} opacity={0.85} data-id="element-105" data-mp="70m3njfo2b6" />)}

      {arcs.map(a => {
      const [x, y] = polar(R, a.midAngle);
      return <circle key={`dot-${a.id}`} cx={x} cy={y} r={5.5} fill={a.dot} data-id="element-106" data-mp="3dnkx3y05xpet" />;
    })}

      {arcs.map((a, i) => <circle key={`pulse-${a.id}`} r={4} fill={a.stroke} data-id="element-107" data-mp="3hpfyf2i3g2sl" data-mpi={i}>
          <animateMotion dur="6s" begin={`${i * 1.2}s`} repeatCount="indefinite" data-id="element-108" data-mp="98l6uj6qy4tt">
            <mpath href={`#${a.id}`} data-id="element-109" data-mp="e6wcsitx7v6r" />
          </animateMotion>
          <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.15;0.85;1" dur="6s" begin={`${i * 1.2}s`} repeatCount="indefinite" data-id="element-110" data-mp="e8hb8jjcvy9w" />
        </circle>)}

      <g opacity={0.9} data-id="element-111" data-mp="s5csgz9rgw11">
        <animateTransform attributeName="transform" type="rotate" from="0 300 300" to="360 300 300" dur="60s" repeatCount="indefinite" data-id="element-112" data-mp="s54ipg8xq6u2" />
        <circle cx={CX} cy={CY} r={CENTER_R + 8} fill="none" stroke="var(--gold)" strokeWidth={1.2} strokeDasharray="120 40 60 90" strokeLinecap="round" data-id="element-113" data-mp="2hlghtkwk7g30" />
        <circle cx={CX} cy={CY} r={CENTER_R + 13} fill="none" stroke="var(--forest-mid)" strokeWidth={1} strokeDasharray="80 100 140 60" strokeLinecap="round" opacity={0.7} data-id="element-114" data-mp="2mdq5sve4662n" />
      </g>

      <circle cx={CX} cy={CY} r={CENTER_R} fill="var(--cream)" stroke="var(--forest-mid)" strokeWidth={1.4} data-id="element-115" data-mp="28mv01ryww53a" />

      {/* Centre statement. Stacked on phones, two lines on larger screens.
          aria-hidden because the whole svg already carries the label. */}
      <g aria-hidden="true" className="lg:hidden" style={{
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.1em'
    }} data-id="element-116" data-mp="3f3gl37rhfe0c">
        <text x={CX} y={CY - 44} textAnchor="middle" fill="var(--forest-mid)" style={{
        fontSize: 24
      }} data-id="element-117" data-mp="1oo67ytdfantt">
          Nature&rsquo;s
        </text>
        <text x={CX} y={CY - 18} textAnchor="middle" fill="var(--forest-mid)" style={{
        fontSize: 24
      }} data-id="element-118" data-mp="1xm8e1nc0k6ny">
          Intelligence
        </text>
        {/* Gold on cream is 2.69:1 and fails AA, so this glyph is decorative
            only: the words either side carry the meaning. */}
        <text x={CX} y={CY + 10} textAnchor="middle" fill="var(--gold)" style={{
        fontSize: 26
      }} data-id="element-119" data-mp="2cjim0mpg6uex">
          +
        </text>
        <text x={CX} y={CY + 36} textAnchor="middle" fill="var(--forest-mid)" style={{
        fontSize: 24
      }} data-id="element-120" data-mp="2hbjyo9qj2n5h">
          Human
        </text>
        <text x={CX} y={CY + 62} textAnchor="middle" fill="var(--forest-mid)" style={{
        fontSize: 24
      }} data-id="element-121" data-mp="2le6suhao8k5u">
          Ingenuity
        </text>
      </g>

      <g aria-hidden="true" className="hidden lg:inline" style={{
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.13em'
    }} data-id="element-122" data-mp="2v9gw18ct2xl7">
        <text x={CX} y={CY - 20} textAnchor="middle" fill="var(--forest-mid)" style={{
        fontSize: 13.5
      }} data-id="element-123" data-mp="30f3uvzbsi5k3">
          Nature&rsquo;s Intelligence
        </text>
        <text x={CX} y={CY + 7} textAnchor="middle" fill="var(--gold)" style={{
        fontSize: 20
      }} data-id="element-124" data-mp="2vgj56tu5z49w">
          +
        </text>
        <text x={CX} y={CY + 31} textAnchor="middle" fill="var(--forest-mid)" style={{
        fontSize: 13.5
      }} data-id="element-125" data-mp="2rgidi7i20wkw">
          Human Ingenuity
        </text>
      </g>

      {nodes.map((n, i) => {
      const [x, y] = polar(R, n.angle);
      const [px, py] = polar(R + NODE_R + 26, n.angle);
      const cos = Math.cos(n.angle * Math.PI / 180);
      const anchor = cos > 0.3 ? 'start' : cos < -0.3 ? 'end' : 'middle';
      const lx = anchor === 'start' ? x + NODE_R + 16 : anchor === 'end' ? x - NODE_R - 16 : px;
      const ly = anchor === 'middle' ? py : y + 5;
      return <g key={n.name} className="eco-node" style={{
        animationDelay: `${i * 1.1}s`
      }} data-id="element-126" data-mp="5wz00ujco595" data-mpi={i}>
            <image href={n.img} x={x - NODE_R} y={y - NODE_R} width={NODE_R * 2} height={NODE_R * 2} preserveAspectRatio="xMidYMid slice" clipPath={`url(#eco-clip-${i})`} data-id="element-127" data-mp="207pbad9uw09g" />
            <circle cx={x} cy={y} r={NODE_R} fill="none" stroke="var(--cream)" strokeWidth={3} data-id="element-128" data-mp="1vew5rn6kcnwb" />
            <text x={lx} y={ly} textAnchor={anchor} fill="var(--foreground)" style={{
          fontSize: 15,
          fontWeight: 600,
          letterSpacing: '0.3em',
          textTransform: 'uppercase'
        }} data-id="element-129" data-mp="1hfmsowdnm9wk">
              {n.name}
            </text>
          </g>;
    })}
    </svg>;
}